import { useEffect, useState } from 'react';

import { cachedIdentityBridge } from '@/tauri/offline-recording/identity';

/**
 * FR-003: can this device capture offline at all?
 *
 * `true` once a prior online login has cached an identity
 * ({@link cachedIdentityBridge}); `false` when a `null` identity means the user
 * has never completed an online login on this device, so offline capture is
 * gated until they do. `undefined` while the async read is still pending — i.e.
 * the answer is not yet known. Callers must treat `undefined` distinctly from
 * `false` so a gate doesn't render the "log in once online first" wall before
 * identity is known (an offline user who DOES have a cached identity would
 * otherwise flash the wall on first render).
 *
 * A lightweight, read-only check (no recording lifecycle state) so gating
 * surfaces such as {@link useOfflineGating} can ask the question without
 * spinning up {@link useOfflineRecorder}. Off-desktop the bridge resolves
 * `null`, so this settles to `false`.
 *
 * A read that REJECTS settles to `false` (fail closed) and is reported by the
 * bridge at the shared invoke, never left pending and never swallowed.
 * `undefined` means "not known yet", and a rejected read is never
 * going to become known — leaving it pending stranded `requiresOnlineLogin` at
 * `false` forever, so an offline clinician got a recorder that silently refused
 * to capture (`useOfflineRecorder`/`useRecordingButton` already treat an
 * unresolved identity as "cannot capture offline") with no explanation on
 * screen. `false` is also the truthful answer: with no identity in hand this
 * device cannot capture offline, and the wall says how to fix that.
 */
export function useCanRecordOffline(): boolean | undefined {
  const [canRecordOffline, setCanRecordOffline] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    let cancelled = false;
    void cachedIdentityBridge
      .read()
      .then((identity) => identity !== null)
      .catch(() => {
        // Fail closed. NOT a silent swallow: the bridge reports the failure at
        // the shared invoke before re-throwing it here (see
        // `cachedIdentityBridge.read`), which is where one denial produces one
        // log line instead of one per mounted consumer. This branch is only the
        // policy — an unreadable identity means this device cannot capture
        // offline — and that answer is what lets the "log in once online first"
        // wall render instead of the gate staying pending forever.
        return false;
      })
      .then((canRecord) => {
        if (!cancelled) {
          setCanRecordOffline(canRecord);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return canRecordOffline;
}
